export interface User {
  id: string;
  email: string;
  name: string;
}

export interface DeviceCodeResponse {
  device_code: string;
  user_code: string;
  verification_uri: string;
  verification_uri_complete: string;
  expires_in: number;
  interval: number;
}

export interface AuthTokens {
  access_token: string;
  refresh_token: string;
  token_type: string;
  expires_in: number;
  expires_at: number;
  scope?: string;
}

export interface UserSession {
  user: User;
  tokens: AuthTokens;
  expiresAt: number;
}
// Auth State Enum
export enum AuthStateType {
  INITIALIZING = 'initializing',
  LOADING = 'loading',
  AUTHENTICATED = 'authenticated',
  UNAUTHENTICATED = 'unauthenticated',
}

// Auth State Interface
export interface AuthState {
  isAuthenticated: boolean;
  user: UserSession['user'] | null;
  authStateType: AuthStateType;
  error: string | null;
}

// Auth Context Interface with methods
export interface AuthContextType extends AuthState {
  logout: () => Promise<void>;
  refreshAuth: () => Promise<void>;
  isLoading: boolean;
}

// Auth0 Device Code Error Types
export enum Auth0DeviceCodeError {
  AUTHORIZATION_PENDING = 'authorization_pending',
  SLOW_DOWN = 'slow_down',
  ACCESS_DENIED = 'access_denied',
  EXPIRED_TOKEN = 'expired_token',
  INVALID_REQUEST = 'invalid_request',
  INVALID_GRANT = 'invalid_grant',
}

export interface DeviceCodeErrorResponse {
  error: Auth0DeviceCodeError;
  error_description?: string;
}

/**
 * Note: null indicates an intentional cancellation (e.g., AbortError) during
 * the token exchange, not a failure at the token endpoint.
 */
export type DeviceCodeExchangeResult =
  | AuthTokens
  | null
  | DeviceCodeErrorResponse;

export const isAuthTokens = (
  result: DeviceCodeExchangeResult,
): result is AuthTokens => {
  return (
    result !== null &&
    typeof result === 'object' &&
    'access_token' in result &&
    'refresh_token' in result &&
    'token_type' in result &&
    'expires_at' in result
  );
};

// Type guard function to check if result is an error response
export function isDeviceCodeError(
  result: DeviceCodeExchangeResult,
): result is DeviceCodeErrorResponse {
  return result !== null && typeof result === 'object' && 'error' in result;
}


